import { useMemo, useState, type DragEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { CircleAlert, Copy, GripVertical, MoreHorizontal, Pencil, Settings2, Trash2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/components/ui/sonner';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { ROW_MENU_CONTENT, ROW_MENU_ITEM, ROW_MENU_ITEM_DANGER } from '@/components/layouts/menu-styles';
import { SETTINGS_TOOLTIP_CLASS } from '@/modules/settings';
import { cn, getApiErrorMessage } from '@/lib/utils';
import type { RuleRecord, RulesCatalog } from '@/types/rules.types';
import { useDeleteTicketRule, useDuplicateTicketRule, useReorderTicketRules, useSetTicketRuleStatus } from '../hooks/use-ticket-rules';
import { moveStep } from '../lib/rule-tree';
import { summarizeRule } from '../lib/rule-summary';
import { RULES_PATH } from './managed-by-rules-notice';
import { RuleStatusPill } from './rule-status-pill';
import { matchedText } from '../lib/labels';
import { conflictHints } from '../lib/rule-conflicts';

interface RuleRowProps {
	rule: RuleRecord;
	index: number;
	count: number;
	catalog: RulesCatalog;
	isAdmin: boolean;
	/** How it interacts with the playbooks around it, when that needs saying. */
	hint?: string;
	isDragging: boolean;
	/** A reorder is on its way to the server; moves wait for it. */
	reordering: boolean;
	onMove: (from: number, to: number) => void;
	onDragStart: (index: number) => void;
	onDragEnd: () => void;
	onDrop: (index: number) => void;
	onDelete: (rule: RuleRecord) => void;
	/** A row inside one card, as on the settings pages, instead of a card of its own. */
	compact: boolean;
	/** The "Matched N times this week" column; left out where the list is narrow. */
	showMatches: boolean;
}

function RuleRow({ rule, index, count, catalog, isAdmin, hint, isDragging, reordering, onMove, onDragStart, onDragEnd, onDrop, onDelete, compact, showMatches }: RuleRowProps) {
	const navigate = useNavigate();
	const setStatus = useSetTicketRuleStatus();
	const duplicate = useDuplicateTicketRule();
	const summary = summarizeRule(rule, catalog);
	const isLive = rule.status === 'live';
	const to = `${RULES_PATH}/${rule.id}`;

	const toggle = async (next: boolean) => {
		try {
			await setStatus.mutateAsync({ id: rule.id, status: next ? 'live' : 'paused' });
			toast.success(next ? `“${rule.name}” is live` : `“${rule.name}” is paused`);
		} catch (error) {
			console.error('[RuleRow.toggle] failed to change the status', { ruleId: rule.id, next, error });
			toast.error(getApiErrorMessage(error, 'Could not change the playbook. Please try again.'));
		}
	};

	const copy = async () => {
		try {
			const created = await duplicate.mutateAsync(rule.id);
			toast.success(`Copied “${rule.name}”`, { action: { label: 'Open', onClick: () => navigate(`${RULES_PATH}/${created.id}`) } });
		} catch (error) {
			console.error('[RuleRow.copy] failed to duplicate', { ruleId: rule.id, error });
			toast.error(getApiErrorMessage(error, 'Could not copy the playbook. Please try again.'));
		}
	};

	const handleDragStart = (event: DragEvent<HTMLButtonElement>) => {
		if (reordering) {
			event.preventDefault();
			return;
		}
		event.dataTransfer.effectAllowed = 'move';
		event.dataTransfer.setData('text/plain', rule.id);
		const row = event.currentTarget.closest('li');
		if (row) event.dataTransfer.setDragImage(row, 16, 24);
		onDragStart(index);
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
		if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
		event.preventDefault();
		if (reordering) return;
		onMove(index, event.key === 'ArrowUp' ? index - 1 : index + 1);
	};

	return (
		<li
			onDragOver={(event) => {
				if (isAdmin) event.preventDefault();
			}}
			onDrop={(event) => {
				event.preventDefault();
				onDrop(index);
			}}
			className={cn(
				compact
					? 'group flex items-center gap-3 bg-white py-3 pr-3 pl-2 transition-[background-color,opacity] hover:bg-gray-50'
					: 'group flex items-center gap-3 rounded-2xl border border-gray-500 bg-white py-3 pr-3 pl-2 transition-[box-shadow,border-color,opacity] hover:border-gray-600 hover:shadow-[0px_3px_12px_0px_rgba(0,0,0,0.06)]',
				isDragging && 'opacity-50'
			)}
		>
			{isAdmin ? (
				<button
					type='button'
					draggable={!reordering}
					aria-disabled={reordering || undefined}
					onDragStart={handleDragStart}
					onDragEnd={onDragEnd}
					onKeyDown={handleKeyDown}
					aria-label={`Move “${rule.name}”, number ${index + 1} of ${count}. Use the arrow keys to move it.`}
					className={cn(
						'flex h-8 w-5 shrink-0 items-center justify-center rounded text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
						reordering ? 'cursor-wait opacity-50' : 'cursor-grab hover:text-ink active:cursor-grabbing'
					)}
				>
					<GripVertical className='size-4' />
				</button>
			) : (
				<span className='w-5 shrink-0' />
			)}
			<span className='w-5 shrink-0 text-center text-[12px] tabular-nums text-gray-700'>{index + 1}</span>
			<div className='flex min-w-0 flex-1 flex-col gap-0.5'>
				<div className='flex min-w-0 items-center gap-2'>
					<Link to={to} className='min-w-[96px] truncate text-[14px] font-medium leading-5 text-ink hover:underline focus-visible:underline'>
						{rule.name}
					</Link>
					{rule.origin === 'system' && (
						<Tooltip>
							<TooltipTrigger asChild>
								<span
									tabIndex={0}
									className='inline-flex shrink-0 items-center gap-1 rounded-full border border-gray-500 px-2 py-px text-[11px] leading-4 text-gray-900 outline-none focus-visible:ring-2 focus-visible:ring-ring/40'
								>
									<Settings2 className='size-3' aria-hidden />
									From your settings
								</span>
							</TooltipTrigger>
							<TooltipContent variant='dark' className={SETTINGS_TOOLTIP_CLASS}>
								Made from this brand’s settings when playbooks were switched on, so nothing changed for your customers. Edit it like any other playbook.
							</TooltipContent>
						</Tooltip>
					)}
				</div>
				<p className='truncate text-[12px] leading-4 text-gray-900' title={summary}>
					{summary}
				</p>
				{hint && (
					<p className='flex min-w-0 items-center gap-1 text-[12px] leading-4 text-amber-700'>
						<CircleAlert className='size-3 shrink-0' aria-hidden />
						<span className='truncate' title={hint}>
							{hint}
						</span>
					</p>
				)}
			</div>
			{showMatches && (
				<span className='hidden w-[170px] shrink-0 text-right text-[12px] tabular-nums text-gray-800 md:block'>{matchedText(rule.matchedLast7Days)}</span>
			)}
			<div className='flex w-[88px] shrink-0 justify-end'>
				<RuleStatusPill status={rule.status} />
			</div>
			{isAdmin && (
				<Switch
					aria-label={`${rule.name} live`}
					checked={isLive}
					disabled={setStatus.isPending && setStatus.variables?.id === rule.id}
					onCheckedChange={(next) => void toggle(next)}
				/>
			)}
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<button
						type='button'
						aria-label={`More for ${rule.name}`}
						className='flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-800 hover:bg-gray-100 hover:text-ink'
					>
						<MoreHorizontal className='size-4' />
					</button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align='end' className={ROW_MENU_CONTENT}>
					<DropdownMenuItem onSelect={() => navigate(to)} className={ROW_MENU_ITEM}>
						<Pencil /> {isAdmin ? 'Edit' : 'Open'}
					</DropdownMenuItem>
					{isAdmin && (
						<>
							<DropdownMenuItem onSelect={() => void copy()} className={ROW_MENU_ITEM}>
								<Copy /> Duplicate
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuItem onSelect={() => onDelete(rule)} className={cn(ROW_MENU_ITEM, ROW_MENU_ITEM_DANGER)}>
								<Trash2 /> Delete
							</DropdownMenuItem>
						</>
					)}
				</DropdownMenuContent>
			</DropdownMenu>
		</li>
	);
}

interface RulesListProps {
	rules: RuleRecord[];
	/** The brand's whole list, in run order, when `rules` shows only part of it. Defaults to `rules`. */
	allRules?: RuleRecord[];
	catalog: RulesCatalog;
	isAdmin: boolean;
	/** Rows inside one card, as on the settings pages. */
	compact?: boolean;
	/** Leave out the "Matched N times this week" column, for a narrow list. */
	hideMatches?: boolean;
}

/**
 * The brand's playbooks in the order they run. Admins drag the handle (or press the arrow keys on it)
 * to reorder, and switch each one live or paused right here.
 *
 * Native drag and drop, like the Sales Agent's conditions: no drag library is installed, and one short
 * list does not need one.
 */
export function RulesList({ rules, allRules = rules, catalog, isAdmin, compact = false, hideMatches = false }: RulesListProps) {
	const reorder = useReorderTicketRules();
	const remove = useDeleteTicketRule();
	const [dragIndex, setDragIndex] = useState<number | null>(null);
	// Kept after the dialog closes, so its title does not empty while it animates out.
	const [deleting, setDeleting] = useState<RuleRecord | null>(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const hints = useMemo(() => conflictHints(allRules), [allRules]);
	const shownIds = useMemo(() => new Set(rules.map((rule) => rule.id)), [rules]);
	/** A hint caused by a playbook listed on another settings page says so: "above" alone would mislead. */
	const hintFor = (id: string): string | undefined => {
		const hint = hints.get(id);
		if (!hint) return undefined;
		// The playbook behind it may be on another page, or be one a settings page edits (not listed).
		return shownIds.has(hint.causedBy) ? hint.text : `${hint.text} It's set up elsewhere in your settings.`;
	};

	const move = (from: number, to: number) => {
		// One move at a time: overlapping reorders could reach the server out of order, and the order
		// decides which playbook wins.
		if (reorder.isPending) return;
		if (to < 0 || to >= rules.length || from === to) return;
		// The playbooks shown keep their slots in the brand's one order; only they swap places.
		const moved = moveStep(rules, from, to);
		const shown = new Set(rules.map((rule) => rule.id));
		let next = 0;
		reorder.mutate(allRules.map((rule) => (shown.has(rule.id) ? (moved[next++]?.id ?? rule.id) : rule.id)), {
			onError: (error) => toast.error(getApiErrorMessage(error, 'Could not change the order. Please try again.'))
		});
	};

	const confirmDelete = async () => {
		if (!deleting) return;
		try {
			await remove.mutateAsync(deleting.id);
			toast.success(`Deleted “${deleting.name}”`);
		} catch (error) {
			console.error('[RulesList.confirmDelete] failed to delete', { ruleId: deleting.id, error });
			toast.error(getApiErrorMessage(error, 'Could not delete the playbook. Please try again.'));
		} finally {
			setConfirmOpen(false);
		}
	};

	return (
		<>
			<p aria-live='polite' className='mb-1 h-4 text-right text-[11px] leading-4 text-gray-800'>
				{reorder.isPending ? 'Saving order…' : ''}
			</p>
			<ol
				aria-label='Playbooks, in the order they run'
				className={cn('flex flex-col', compact ? 'divide-y divide-dashed divide-gray-500 overflow-hidden rounded-2xl border border-gray-500 bg-white' : 'gap-2')}
			>
				{rules.map((rule, index) => (
					<RuleRow
						key={rule.id}
						rule={rule}
						index={index}
						count={rules.length}
						catalog={catalog}
						isAdmin={isAdmin}
						compact={compact}
						showMatches={!hideMatches}
						hint={hintFor(rule.id)}
						isDragging={dragIndex === index}
						reordering={reorder.isPending}
						onMove={move}
						onDragStart={setDragIndex}
						onDragEnd={() => setDragIndex(null)}
						onDrop={(to) => {
							if (dragIndex !== null) move(dragIndex, to);
							setDragIndex(null);
						}}
						onDelete={(target) => {
							setDeleting(target);
							setConfirmOpen(true);
						}}
					/>
				))}
			</ol>
			<ConfirmDialog
				open={confirmOpen}
				title={`Delete “${deleting?.name ?? ''}”?`}
				description='It stops running straight away. Its past activity stays in the log.'
				confirmLabel='Delete'
				destructive
				isPending={remove.isPending}
				onConfirm={() => void confirmDelete()}
				onCancel={() => setConfirmOpen(false)}
			/>
		</>
	);
}
